<script lang="ts" module>
  import type { ImageUrlBuilder } from '@sanity/image-url/lib/types/builder';

  export type SchemaInput = Record<string, any>;

  type ProcessKeyFn = (
    key: string,
    value: any
  ) =>
    | { type: 'rename'; key: string }
    | { type: 'update'; value: any }
    | { type: 'delete' }
    | undefined;

  function traverseSchemaInput(input: SchemaInput, processKey: ProcessKeyFn) {
    const traverse = (input: SchemaInput) => {
      if (input instanceof Array) {
        for (let i = 0; i < input.length; i++) {
          traverse(input[i]);
        }
      } else if (input instanceof Object) {
        for (const key in input) {
          traverse(input[key]);

          const result = processKey(key, input[key]);

          if (!result) {
            continue;
          }

          switch (result.type) {
            case 'delete':
              delete input[key];
              break;
            case 'rename':
              input[result.key] = input[key];
              delete input[key];
              break;
            case 'update':
              input[key] = result.value;
              break;
          }
        }
      }
    };

    traverse(input);
  }

  export function replaceAtSymbol(input: SchemaInput) {
    traverseSchemaInput(input, (key) => {
      if (key.startsWith('at_')) {
        return { type: 'rename', key: key.replace('at_', '@') };
      }
    });
  }

  export function deleteSanityKeys(input: SchemaInput) {
    traverseSchemaInput(input, (key) => {
      if (key === '_key') {
        return { type: 'delete' };
      }
    });
  }

  export function generateImageUrls(input: SchemaInput, urlBuilder: ImageUrlBuilder) {
    traverseSchemaInput(input, (key, value) => {
      if (value._type === 'image') {
        return {
          type: 'update',
          value: urlBuilder.image(input[key]).auto('format').url()
        };
      }
    });
  }
</script>

<script lang="ts">
  import { getContext } from 'svelte';
  import { key, type SanityContext } from '$lib/context/sanity';
  import cloneDeep from 'lodash/cloneDeep';

  interface Props {
    input: SchemaInput;
    pageUrlFallback: string | undefined;
  }

  let { input, pageUrlFallback }: Props = $props();
  let { urlBuilder } = getContext<SanityContext>(key);

  let schema = $derived.by(() => {
    let result = cloneDeep(input);
    deleteSanityKeys(result);
    generateImageUrls(result, urlBuilder);
    replaceAtSymbol(result);
    if (pageUrlFallback && !result.url) {
      result.url = pageUrlFallback;
    }
    return result;
  });
</script>

<template>
  {@html `<script type="application/ld+json">${JSON.stringify(schema)}</script>`}
</template>
